import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface UIState {
  // Sidebar & Navigation
  sidebarOpen: boolean;
  mobileMenuOpen: boolean;
  
  // Modals
  activeModal: string | null;
  modalData: any;
  
  // Theme
  theme: 'light' | 'dark' | 'system';
  
  // Notifications
  showNotifications: boolean;
  unreadNotifications: number;
  
  // Preferences
  preferredView: 'grid' | 'list';
  itemsPerPage: number;
  
  // Loading states
  globalLoading: boolean;
  loadingMessage: string | null;
  
  // Actions
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  
  openModal: (modalId: string, data?: any) => void;
  closeModal: () => void;
  
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  
  toggleNotifications: () => void;
  setUnreadNotifications: (count: number) => void;
  
  setPreferredView: (view: 'grid' | 'list') => void;
  setItemsPerPage: (count: number) => void;
  
  setGlobalLoading: (loading: boolean, message?: string) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      // Initial state
      sidebarOpen: false,
      mobileMenuOpen: false,
      activeModal: null,
      modalData: null,
      theme: 'system',
      showNotifications: false,
      unreadNotifications: 0,
      preferredView: 'list',
      itemsPerPage: 10,
      globalLoading: false,
      loadingMessage: null,

      // Actions
      toggleSidebar: () => set((state) => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: (open) => set({ sidebarOpen: open }),
      
      toggleMobileMenu: () => set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen })),
      setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),
      
      openModal: (modalId, data) => set({ activeModal: modalId, modalData: data }),
      closeModal: () => set({ activeModal: null, modalData: null }),
      
      setTheme: (theme) => {
        set({ theme });
        // Apply theme to document
        if (theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },
      
      toggleNotifications: () => set((state) => ({ showNotifications: !state.showNotifications })),
      setUnreadNotifications: (count) => set({ unreadNotifications: count }),
      
      setPreferredView: (view) => set({ preferredView: view }),
      setItemsPerPage: (count) => set({ itemsPerPage: count }),
      
      setGlobalLoading: (loading, message) => set({ 
        globalLoading: loading, 
        loadingMessage: message || null 
      }),
    }),
    {
      name: 'ui-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        theme: state.theme,
        preferredView: state.preferredView,
        itemsPerPage: state.itemsPerPage,
      }),
    }
  )
);

// Selector hooks
export const useSidebarOpen = () => useUIStore((state) => state.sidebarOpen);
export const useMobileMenuOpen = () => useUIStore((state) => state.mobileMenuOpen);
export const useActiveModal = () => useUIStore((state) => state.activeModal);
export const useModalData = () => useUIStore((state) => state.modalData);
export const useTheme = () => useUIStore((state) => state.theme);
export const usePreferredView = () => useUIStore((state) => state.preferredView);
export const useItemsPerPage = () => useUIStore((state) => state.itemsPerPage);
export const useGlobalLoading = () => useUIStore((state) => ({
  isLoading: state.globalLoading,
  message: state.loadingMessage,
}));

// Modal helpers
export const useModal = (modalId: string) => {
  const activeModal = useActiveModal();
  const modalData = useModalData();
  const { openModal, closeModal } = useUIStore();
  
  return {
    isOpen: activeModal === modalId,
    data: activeModal === modalId ? modalData : null,
    open: (data?: any) => openModal(modalId, data),
    close: closeModal,
  };
};
